import type { NotificationState, ProjectStatus } from './types';

export const DAY_MS = 24 * 60 * 60 * 1_000;
export const OUTBOX_BASE_BACKOFF_MS = 15 * 60 * 1_000;
export const MEDIA_GRACE_PERIOD_MS = 7 * DAY_MS;

export type ExpiryWarningEvent = 'expiry_approaching_45d' | 'expiry_final_55d';

export interface RetryableNotification {
  state: NotificationState;
  attempts: number;
  updated_at: number;
}

export function notificationBackoffMs(attempts: number): number {
  const safeAttempts = Math.max(0, Math.floor(attempts));
  return OUTBOX_BASE_BACKOFF_MS * 2 ** safeAttempts;
}

export function isNotificationDue(row: RetryableNotification, now: number): boolean {
  if (row.state === 'pending') return true;
  if (row.state !== 'failed') return false;
  return now >= row.updated_at + notificationBackoffMs(row.attempts);
}

export function expiryWarningsDue(
  expiryAt: number | null,
  now: number,
  alreadyEmitted: ReadonlySet<ExpiryWarningEvent>,
): ExpiryWarningEvent[] {
  if (expiryAt === null) return [];
  const remaining = expiryAt - now;
  const due: ExpiryWarningEvent[] = [];
  if (remaining <= 15 * DAY_MS && !alreadyEmitted.has('expiry_approaching_45d')) {
    due.push('expiry_approaching_45d');
  }
  if (remaining <= 5 * DAY_MS && !alreadyEmitted.has('expiry_final_55d')) {
    due.push('expiry_final_55d');
  }
  return due;
}

export function shouldExpireProject(
  status: ProjectStatus,
  expiryAt: number | null,
  now: number,
): boolean {
  return status === 'active' && expiryAt !== null && now > expiryAt;
}

export function isMediaGraceElapsed(transitionAt: number | null, now: number): boolean {
  return transitionAt !== null && now > transitionAt + MEDIA_GRACE_PERIOD_MS;
}

export function timeZoneParts(
  timeZone: string,
  now: number,
): { date: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(now));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '0';
  const rawHour = Number(value('hour'));
  return {
    date: `${value('year')}-${value('month')}-${value('day')}`,
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(value('minute')),
  };
}

export function isMelbourneDailyWindow(scheduledTime: number): boolean {
  const { hour, minute } = timeZoneParts('Australia/Melbourne', scheduledTime);
  return hour === 3 && minute < 15;
}

export function nextMelbourneDailyRunAtOrAfter(instant: number): number {
  const cronIntervalMs = 15 * 60_000;
  let candidate = Math.ceil(instant / cronIntervalMs) * cronIntervalMs;
  const searchEnd = candidate + 2 * DAY_MS;
  while (candidate <= searchEnd) {
    const { hour, minute } = timeZoneParts('Australia/Melbourne', candidate);
    if (hour === 3 && minute === 0) return candidate;
    candidate += cronIntervalMs;
  }
  throw new Error('Could not find the next Melbourne daily run.');
}
